import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  DeleteForever as DeleteForeverIcon,
  FiberManualRecord as BulletIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import usersService from '../services/usersService';
import { authService } from '../services/authService';
import AppSnackbar from './ui/AppSnackbar';
import { useSnackbar } from '../hooks/useSnackbar';

const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || 'https://agendario.com.br';

const CONSEQUENCES = [
  'Todos os seus locais e configurações de serviços',
  'Profissionais e agendas vinculados',
  'Histórico completo de agendamentos',
  'Clientes e contatos cadastrados',
  'Dados de pagamento configurados',
  'Suas páginas públicas de compartilhamento',
];

export default function DeleteAccountDialog({ open, onClose, userEmail }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { snackbar, showError, hideSnackbar } = useSnackbar();

  const isConfirmed = confirmEmail.trim().toLowerCase() === (userEmail || '').toLowerCase();

  const handleClose = () => {
    if (loading) return;
    setConfirmEmail('');
    hideSnackbar();
    onClose();
  };

  const handleDelete = async () => {
    if (!isConfirmed || loading) return;

    setLoading(true);

    try {
      await usersService.deleteAccount();
      authService.removeToken();
      window.location.href = PUBLIC_URL;
    } catch {
      showError('Ocorreu um erro ao excluir a conta. Tente novamente.');
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <DeleteForeverIcon color="error" fontSize="large" />
          <Typography variant="h6" component="span" fontWeight={700}>
            Excluir Conta
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Esta ação é permanente e irreversível.
            </Typography>
            <Typography variant="body2">
              Todos os seus dados serão excluídos imediatamente e não poderão ser recuperados.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Os seguintes dados serão permanentemente removidos:
          </Typography>

          <List dense disablePadding sx={{ mb: 2 }}>
            {CONSEQUENCES.map((item) => (
              <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <BulletIcon sx={{ fontSize: 8, color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>

          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <Typography variant="body2">
              Se criar uma nova conta com o mesmo e-mail, ela estará{' '}
              <strong>completamente vazia</strong> — seus dados antigos não poderão ser recuperados.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Para confirmar, digite seu e-mail:{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {userEmail}
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={userEmail}
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={loading}
              autoComplete="off"
              inputProps={{ 'aria-label': 'Confirmar e-mail para excluir conta' }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined" color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            disabled={!isConfirmed || loading}
            variant="contained"
            color="error"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
          >
            {loading ? 'Excluindo...' : 'Excluir Conta'}
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}

DeleteAccountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
};
